// Returns a wrapper that runs at most `maxConcurrent` tasks at a time and queues
// the rest in arrival order. Every caller of a given wrapper shares one queue, so
// it must be created once at module scope, not per component.
export const limitConcurrency = (maxConcurrent: number) => {
  let running = 0;
  const waiting: { resume: () => void }[] = [];

  // Hand the slot straight to the next waiter rather than releasing and
  // re-acquiring it — a task arriving in between would otherwise find the slot
  // free and push us over the limit.
  const releaseSlot = () => {
    const resumeNext = waiting.shift();
    if (resumeNext) {
      resumeNext.resume();
      return;
    }
    running--;
  };

  // Aborting removes a task that has not started yet, so a caller that has gone
  // away stops holding a place in front of the tasks that are still wanted. A task
  // already holding a slot is no longer in `waiting`, so it runs to completion and
  // still releases its slot.
  const waitForSlot = (signal?: AbortSignal) => {
    let resume!: () => void;
    let abandon!: (reason: unknown) => void;
    const slot = new Promise<void>((resolve, reject) => {
      resume = () => resolve();
      abandon = reject;
    });

    const waiter = { resume };
    waiting.push(waiter);

    signal?.addEventListener(
      'abort',
      () => {
        const queuePosition = waiting.indexOf(waiter);
        if (queuePosition === -1) return;
        waiting.splice(queuePosition, 1);
        abandon(signal.reason);
      },
      { once: true }
    );

    return slot;
  };

  return async <T>(task: () => Promise<T>, signal?: AbortSignal): Promise<T> => {
    if (signal?.aborted) throw signal.reason;

    if (running >= maxConcurrent) {
      await waitForSlot(signal);
    } else {
      running++;
    }

    try {
      return await task();
    } finally {
      releaseSlot();
    }
  };
};
